import { useState } from 'react';
import { X } from 'lucide-react';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: (data: OnboardingData) => void;
  onSkip: () => void;
  needsEmailConfirmation?: boolean;
}

interface OnboardingData {
  interviewType?: string;
  confidenceLevel?: string;
  focusAreas?: string[];
  profilePhoto?: File | null;
  resume?: File | null;
  bio?: string;
}

export default function OnboardingModal({ isOpen, onComplete, onSkip, needsEmailConfirmation }: OnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({
    focusAreas: [],
  });

  const totalSteps = 4;

  const updateProgress = (step: number) => {
    return (step / totalSteps) * 100;
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(data);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const selectInterviewType = (type: string) => {
    setData({ ...data, interviewType: type });
  };

  const selectConfidence = (level: string) => {
    setData({ ...data, confidenceLevel: level });
  };

  const toggleFocusArea = (area: string) => {
    const current = data.focusAreas || [];
    if (current.includes(area)) {
      setData({ ...data, focusAreas: current.filter(a => a !== area) });
    } else {
      setData({ ...data, focusAreas: [...current, area] });
    }
  };

  const handleFileUpload = (type: 'profilePhoto' | 'resume', file: File | null) => {
    setData({ ...data, [type]: file });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 animate-fade-in">
      <div className="w-full max-w-xl mx-4 bg-white rounded-3xl shadow-xl overflow-hidden animate-scale-in">
        {/* Header / progress */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-slate-900">
              Let's tailor your interview practice
            </h3>
            <button
              onClick={onSkip}
              className="text-slate-400 hover:text-slate-600 text-sm transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-emerald-600 rounded-full transition-all duration-300"
                style={{ width: `${updateProgress(currentStep)}%` }}
              ></div>
            </div>
            <span className="text-xs text-slate-500">
              Step {currentStep} of {totalSteps}
            </span>
          </div>
        </div>

        {/* Steps */}
        <div className="px-6 py-6 min-h-[400px]">
          {/* Step 1: Interview Type */}
          {currentStep === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-1">
                  What type of interview are you preparing for?
                </h4>
                <p className="text-sm text-slate-500">
                  This helps us choose the right question style for you.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => selectInterviewType('technical')}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                    data.interviewType === 'technical'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60'
                  }`}
                >
                  <p className="font-medium text-slate-800">Technical / IT role</p>
                  <p className="text-xs text-slate-500 mt-1">Developer, QA, DevOps, etc.</p>
                </button>
                <button
                  onClick={() => selectInterviewType('business')}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                    data.interviewType === 'business'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60'
                  }`}
                >
                  <p className="font-medium text-slate-800">Business / non-technical</p>
                  <p className="text-xs text-slate-500 mt-1">PM, marketing, HR, etc.</p>
                </button>
                <button
                  onClick={() => selectInterviewType('graduate')}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                    data.interviewType === 'graduate'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60'
                  }`}
                >
                  <p className="font-medium text-slate-800">Graduate / first role</p>
                  <p className="text-xs text-slate-500 mt-1">Internship or junior role.</p>
                </button>
                <button
                  onClick={() => selectInterviewType('leadership')}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${
                    data.interviewType === 'leadership'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60'
                  }`}
                >
                  <p className="font-medium text-slate-800">Leadership / management</p>
                  <p className="text-xs text-slate-500 mt-1">Team lead, manager, director.</p>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Confidence Level */}
          {currentStep === 2 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-1">
                  How confident do you feel about interviews right now?
                </h4>
                <p className="text-sm text-slate-500">
                  We'll adjust the difficulty and tone of feedback.
                </p>
              </div>

              <div className="space-y-2">
                {[
                  { value: 'nervous', label: "I'm very nervous and don't know where to start." },
                  { value: 'beginner', label: "I've done a few interviews, but I need structure." },
                  { value: 'confident', label: "I'm fairly confident and want tougher questions." },
                  { value: 'refresh', label: 'I just want a quick refresh before a real interview.' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition text-sm ${
                      data.confidenceLevel === option.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60'
                    }`}
                  >
                    <input
                      type="radio"
                      name="confidence"
                      checked={data.confidenceLevel === option.value}
                      onChange={() => selectConfidence(option.value)}
                      className="accent-emerald-600"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Focus Areas */}
          {currentStep === 3 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-1">
                  Which skills do you want to focus on first?
                </h4>
                <p className="text-sm text-slate-500">
                  Choose one or more practice areas.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[
                  { value: 'star', label: 'Clear, structured answers (STAR)' },
                  { value: 'technical', label: 'Technical depth & examples' },
                  { value: 'confidence', label: 'Confidence, tone & pacing' },
                  { value: 'difficult', label: 'Handling difficult questions' }
                ].map((area) => (
                  <label
                    key={area.value}
                    className={`flex items-center gap-2 p-3 rounded-2xl border cursor-pointer transition ${
                      data.focusAreas?.includes(area.value)
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={data.focusAreas?.includes(area.value)}
                      onChange={() => toggleFocusArea(area.value)}
                      className="accent-emerald-600"
                    />
                    {area.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Uploads & Bio */}
          {currentStep === 4 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-1">
                  Add a few career details (optional)
                </h4>
                <p className="text-sm text-slate-500">
                  This helps the AI interviewer give more realistic scenarios.
                </p>
                {needsEmailConfirmation && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    File uploads will be available after you confirm your email
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Profile photo
                    </label>
                    <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 rounded-2xl py-6 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/40 transition">
                      <span className="text-xs font-medium text-slate-700">Upload photo</span>
                      <span className="text-[11px] text-slate-400">JPG or PNG, up to 5MB</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload('profilePhoto', e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                    {data.profilePhoto && (
                      <p className="text-xs text-emerald-600 mt-1">✓ {data.profilePhoto.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      CV / Resume
                    </label>
                    <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 rounded-2xl py-6 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/40 transition">
                      <span className="text-xs font-medium text-slate-700">Upload resume</span>
                      <span className="text-[11px] text-slate-400">PDF or DOCX, up to 10MB</span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileUpload('resume', e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                    {data.resume && (
                      <p className="text-xs text-emerald-600 mt-1">✓ {data.resume.name}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Short personal bio
                  </label>
                  <textarea
                    rows={4}
                    value={data.bio || ''}
                    onChange={(e) => setData({ ...data, bio: e.target.value })}
                    className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/70 focus:border-emerald-500"
                    placeholder="Tell us about your background, the roles you're aiming for, and anything else you'd like the interviewer to know."
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="px-6 pb-5 pt-3 border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition"
          >
            Skip for now
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className="px-4 py-2 text-xs rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              className="px-5 py-2 text-xs rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition"
            >
              {currentStep === totalSteps ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
